// "Manage desyr ID" (36-desyr-id.md, My account with desyr ID and Behaviour 3, point 4; 07, You;
// v1.58.48): the desyr ID's own account page in a new tab, which shares the browser's desyr ID session.
// In the installed app on an iPhone or iPad (storage not Safari's) a new tab would ask for a second
// sign-in, so there it stays a same-window navigation that comes back, with no open-in-new symbol.
import manageAccounts from '@material-symbols/svg-400/outlined/manage_accounts.svg?raw';
import openInNew from '@material-symbols/svg-400/outlined/open_in_new.svg?raw';
import passkey from '@material-symbols/svg-400/outlined/passkey.svg?raw';
import { useId } from 'react';
import { Icon } from '../blocks/icons';
import { config } from '../core/config';
import { frameIcons } from '../frame/icons';
import { t } from '../i18n';
import { ios, isInstalled } from '../install/install';
import { keepReturn, readId } from './store';

/** The row of My account (`card`) or of the phone's You (`you`); only with a desyr ID. */
export function ManageIdRow({ look }: { look: 'card' | 'you' }) {
  const line = useId();
  const account = config().desyrId?.account;
  if (!account || !readId()) return null;
  const here = ios() && isInstalled();
  const name = t('id.manageId');
  // Named by its title alone (its line is its description), with ", opens in a new tab" where it does.
  const how = here
    ? { onClick: () => keepReturn(location.pathname + location.search), 'aria-label': name }
    : { target: '_blank', rel: 'noopener', 'aria-label': name + t('id.newTab') };
  const end = here ? look === 'you' && <Icon svg={frameIcons.chevronRight} size={20} className="you-more" /> : <Icon svg={openInNew} size={16} className="manage-id-new" />;
  const icon = <span className="settings-icon"><Icon svg={manageAccounts} size={20} /></span>;
  return look === 'card' ? (
    <a className="settings-card manage-id" href={account} data-desyr-manage-id={here ? 'window' : 'tab'} aria-describedby={line} {...how}>
      <div className="settings-card-row">
        {icon}
        <div className="settings-card-text">
          <div className="settings-card-title">{name}</div>
          <div className="settings-card-line" id={line}>{t('id.manageLine')}</div>
        </div>
        {end}
      </div>
    </a>
  ) : (
    <a className="row you-row manage-id" href={account} data-desyr-manage-id={here ? 'window' : 'tab'} aria-describedby={line} {...how}>
      {icon}
      <span className="manage-id-text">
        <span className="row-name">{name}</span>
        <small id={line}>{t('id.manageLine')}</small>
      </span>
      {end}
    </a>
  );
}

/**
 * "Add a passkey on this device" (36, Behaviour 7.1, case A; v1.58.46): desyr ID's own page for a new
 * passkey in the app's window, coming back here (Behaviour 3); in My account and the phone's You.
 */
export function AddPasskeyRow({ look }: { look: 'card' | 'you' }) {
  const account = config().desyrId?.account;
  if (!account || !readId()) return null;
  const back = () => keepReturn(location.pathname + location.search);
  const icon = <span className="settings-icon"><Icon svg={passkey} size={20} /></span>;
  return look === 'card' ? (
    <a className="settings-card manage-id" href={account} onClick={back}>
      <div className="settings-card-row">
        {icon}
        <div className="settings-card-text">
          <div className="settings-card-title">{t('id.addPasskeyDevice')}</div>
        </div>
      </div>
    </a>
  ) : (
    <a className="row you-row manage-id" href={account} onClick={back}>
      {icon}
      <span className="row-name">{t('id.addPasskeyDevice')}</span>
      <Icon svg={frameIcons.chevronRight} size={20} className="you-more" />
    </a>
  );
}
